import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingBag, Star, Eye } from 'lucide-react';
import { Button } from './ui/button';
import { useWishlist } from '../context/WishlistContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import QuickViewModal from './QuickViewModal';

const ProductCard = ({ product, hideWishlistButton = false, onQuickView, priority = false }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showQuickView, setShowQuickView] = useState(false);
  const { isInWishlist, toggleWishlist } = useWishlist();
  const { addToCart } = useCart();

  // Defensive: don't render if product is missing critical data.
  if (!product || !product.id) return null;

  const inWishlist = isInWishlist(product.id);

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await addToCart(product.id, 1);
      toast.success("Added to bag", { description: `${product.name} has been added to your shopping bag.` });
    } catch (error) {
      toast.error("Error", { description: 'Failed to add to cart. Please try again.' });
    }
  };

  const handleToggleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      await toggleWishlist(product.id);
      toast.success(inWishlist ? "Removed from wishlist" : "Added to wishlist", { 
        description: inWishlist ? `${product.name} removed from wishlist` : `${product.name} added to wishlist`
      });
    } catch (error) {
      toast.error("Error", { description: 'Failed to update wishlist. Please try again.' });
    }
  };

  const handleQuickView = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onQuickView) {
      onQuickView(product);
    } else {
      setShowQuickView(true);
    }
  };

  // Calculate price display
  const getPriceDisplay = () => {
    if (product.has_variants && product.variants?.length > 1) {
      const prices = product.variants.filter(v => v.is_active !== false).map(v => v.price);
      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);
      return minPrice !== maxPrice 
        ? `£${minPrice.toFixed(2)} – £${maxPrice.toFixed(2)}`
        : `£${minPrice.toFixed(2)}`;
    }
    return `£${Number(product.price ?? 0).toFixed(2)}`;
  };

  return (
    <>
      <article
        className="group"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        data-testid={`product-card-${product.id}`}
      >
        <Link
          to={`/product/${product.id}`}
          aria-label={`View ${product.name} - ${getPriceDisplay()}`}
          className="block"
        >
          {/* Image Container */}
          <div className="relative aspect-[3/4] overflow-hidden bg-brand-ivory-deep rounded-luxe-lg mb-5 transition-all duration-500 group-hover:shadow-luxe">
            <img
              src={product.image || '/lotus.png'}
              alt={product.name || 'Mirevy product'}
              width="400"
              height="533"
              loading={priority ? "eager" : "lazy"}
              decoding="async"
              onError={(e) => { e.currentTarget.src = '/lotus.png'; }}
              className={`w-full h-full object-cover transition-transform duration-[900ms] ease-out ${
                isHovered ? 'scale-[1.08]' : 'scale-100'
              } ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
              onLoad={() => setImageLoaded(true)}
            />
            
            {/* Loading skeleton */}
            {!imageLoaded && (
              <div className="absolute inset-0 bg-brand-ivory-deep animate-pulse" />
            )}

            {/* Badges - Top Left */}
            <div className="absolute top-4 left-4 flex flex-col gap-2">
              {product.new && (
                <span className="bg-brand-espresso text-brand-ivory text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 font-medium rounded-full">
                  New
                </span>
              )}
              {product.bestseller && (
                <span className="bg-white/90 backdrop-blur text-brand-espresso text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 font-medium rounded-full border border-brand-blush">
                  Bestseller
                </span>
              )}
              {product.originalPrice && (
                <span className="bg-brand-rose-gold text-white text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 font-medium rounded-full">
                  Sale
                </span>
              )}
            </div>

            {/* Wishlist Button - Top Right */}
            {!hideWishlistButton && (
              <button
                onClick={handleToggleWishlist}
                className={`absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/85 backdrop-blur-md rounded-full transition-all duration-300 hover:bg-white hover:scale-110 ${
                  isHovered ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2'
                }`}
                aria-label={inWishlist ? `Remove ${product.name} from wishlist` : `Add ${product.name} to wishlist`}
                data-testid={`wishlist-btn-${product.id}`}
              >
                <Heart
                  size={18}
                  className={`transition-colors duration-200 ${inWishlist ? 'fill-brand-rose-gold text-brand-rose-gold' : 'text-brand-espresso'}`}
                  strokeWidth={1.5}
                />
              </button>
            )}

            {/* Hover Actions - Bottom */}
            <div
              className={`absolute bottom-3 left-3 right-3 transition-all duration-500 ${
                isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <div className="flex gap-2">
                <Button
                  onClick={handleAddToCart}
                  className="flex-1 bg-white/95 backdrop-blur text-brand-espresso hover:bg-brand-espresso hover:text-white rounded-full h-11 text-[11px] uppercase tracking-[0.2em] font-medium transition-colors duration-300 shadow-luxe-sm"
                  data-testid={`add-to-cart-btn-${product.id}`}
                >
                  <ShoppingBag size={14} className="mr-2" strokeWidth={1.5} />
                  Add to Bag
                </Button>
                <Button
                  onClick={handleQuickView}
                  variant="outline"
                  className="w-11 h-11 p-0 bg-white/95 hover:bg-white border-0 rounded-full shadow-luxe-sm"
                  aria-label={`Quick view ${product.name}`}
                  data-testid={`quick-view-btn-${product.id}`}
                >
                  <Eye size={16} strokeWidth={1.5} />
                </Button>
              </div>
            </div>
          </div>

          {/* Product Info */}
          <div className="space-y-1.5 px-1">
            {/* Brand */}
            <p className="text-[10px] uppercase tracking-[0.25em] text-brand-rose-gold font-medium">
              {product.brand}
            </p>
            
            {/* Name */}
            <h3 className="font-serif text-[17px] text-brand-espresso group-hover:text-brand-rose-gold transition-colors duration-300 line-clamp-2 leading-snug">
              {product.name}
            </h3>

            {/* Rating */}
            {product.rating > 0 && (
              <div className="flex items-center gap-2">
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={12}
                      className={i < Math.round(product.rating) ? 'fill-brand-gold text-brand-gold' : 'text-brand-nude'}
                      strokeWidth={1}
                    />
                  ))}
                </div>
                <span className="text-xs text-brand-taupe">
                  ({product.reviewCount || 0})
                </span>
              </div>
            )}

            {/* Price */}
            <div className="flex items-center gap-3 pt-1">
              <span className="font-medium text-brand-espresso text-[15px]">
                {getPriceDisplay()}
              </span>
              {product.originalPrice && !product.has_variants && (
                <span className="text-sm text-brand-taupe line-through">
                  £{product.originalPrice.toFixed(2)}
                </span>
              )}
            </div>

            {/* Size indicator for variants */}
            {product.has_variants && product.variants?.length > 1 && (
              <p className="text-xs text-brand-taupe">
                {product.variants.length} sizes available
              </p>
            )}
          </div>
        </Link>
      </article>

      {/* Quick View Modal */}
      <QuickViewModal
        product={product}
        isOpen={showQuickView}
        onClose={() => setShowQuickView(false)}
      />
    </>
  );
};

export default ProductCard;
